import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { RefreshCw, Play, Pause, ShieldAlert, PiggyBank, Scale, Loader2, CalendarClock, Rocket } from 'lucide-react';
import RoulettePrizeFulfillment from './RoulettePrizeFulfillment';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('access_token') || sessionStorage.getItem('access_token')}`,
});

const fmtN = (v) => Number(v || 0).toLocaleString('pt-BR');
const pct = (v) => `${(Number(v || 0) * 100).toFixed(1)}%`;

const STATUS_STYLE = {
  ACTIVE: 'bg-green-100 text-green-700',
  PAUSED: 'bg-red-100 text-red-700',
  DRAFT: 'bg-gray-100 text-gray-600',
  ENDED: 'bg-slate-200 text-slate-600',
};

const Stat = ({ label, value, accent, testId, hint }) => (
  <div className="rounded-lg border bg-white p-3" data-testid={testId} title={hint}>
    <p className="text-[11px] uppercase tracking-wide text-gray-500">{label}</p>
    <p className={`text-lg font-black ${accent || 'text-gray-900'}`}>{value}</p>
    {hint && <p className="mt-0.5 text-[10px] leading-tight text-gray-400">{hint}</p>}
  </div>
);

export default function RouletteCampaignManagement() {
  const [m, setM] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [seedAmount, setSeedAmount] = useState('');
  const [transferForm, setTransferForm] = useState({ from_pool: 'regular_pool', to_pool: 'jackpot_pool', amount: '' });

  const doTransfer = async () => {
    const amount = parseInt(transferForm.amount, 10);
    if (!amount || amount <= 0) return toast.error('Informe o valor da transferência');
    if (transferForm.from_pool === transferForm.to_pool) return toast.error('Escolha fundos diferentes');
    setBusy(true);
    try {
      const r = await axios.post(`${API}/event-fund/admin/transfer-pool`, {
        campaign_id: 'roleta-azulzinhas',
        from_pool: transferForm.from_pool,
        to_pool: transferForm.to_pool,
        amount,
      }, { headers: authHeaders() });
      toast.success(`Transferido ${fmtN(r.data.amount)} — Jackpot: ${fmtN(r.data.pools.jackpot_pool)} | 2X: ${fmtN(r.data.pools.regular_pool)} | Reserva: ${fmtN(r.data.pools.safety_reserve)}`);
      setTransferForm({ ...transferForm, amount: '' });
      await load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Erro na transferência');
    }
    setBusy(false);
  };
  const [allocAmount, setAllocAmount] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reconcile, setReconcile] = useState(null);
  const [boostTiers, setBoostTiers] = useState([]);
  const [regularTiers, setRegularTiers] = useState([]);
  const [editions, setEditions] = useState([]);
  const [daily, setDaily] = useState(null);

  const loadDaily = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/event-campaigns/roulette/daily-report?days=14`, { headers: authHeaders() });
      setDaily(r.data);
    } catch { /* noop */ }
  }, []);

  const loadEditions = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/event-campaigns/roulette/editions`, { headers: authHeaders() });
      setEditions(r.data.editions || []);
    } catch { /* noop */ }
  }, []);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/event-campaigns/roulette/metrics`, { headers: authHeaders() });
      setM(r.data);
      setBoostTiers((r.data.jackpot_boost?.tiers || []).map((t) => ({
        min_pool: String(t.min_pool),
        percent: String(t.prob_100x / 10000),
      })));
      setRegularTiers((r.data.regular_boost?.tiers || []).map((t) => ({
        min_pool: String(t.min_pool),
        percent: String(t.prob_2x / 10000),
      })));
    } catch { toast.error('Erro ao carregar métricas da roleta'); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); loadEditions(); loadDaily(); }, [load, loadEditions, loadDaily]);

  const changeStatus = async (status, reason) => {
    setBusy(true);
    try {
      await axios.post(`${API}/event-campaigns/roulette/status`, { status, reason },
        { headers: authHeaders() });
      toast.success(`Campanha → ${status}`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao mudar status'); }
    setBusy(false);
  };

  const startNewEdition = async () => {
    if (!window.confirm('🆕 NOVA EDIÇÃO: a edição atual será ARQUIVADA para auditoria e o evento reinicia ZERADO — ranking, ganhadores e pools voltam do zero. O saldo restante dos pools é devolvido ao Fundo Global. Confirmar?')) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/event-campaigns/roulette/new-edition`, {},
        { headers: authHeaders() });
      toast.success(`Edição #${r.data.archived_edition} arquivada! ${fmtN(r.data.swept_total)} devolvidos ao Fundo Global. Edição #${r.data.campaign.edition} iniciada 🎉`);
      await load(); await loadEditions();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao iniciar nova edição'); }
    setBusy(false);
  };

  const resumeEdition = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/event-campaigns/roulette/resume`, {},
        { headers: authHeaders() });
      toast.success(`Edição #${r.data.campaign.edition || 1} retomada — ranking, ganhadores e pools preservados ▶️`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao retomar'); }
    setBusy(false);
  };

  const doSeed = async () => {
    const amount = parseInt(seedAmount, 10);
    if (!amount || amount <= 0) return toast.error('Informe o valor do aporte');
    setBusy(true);
    try {
      const r = await axios.post(`${API}/event-fund/admin/seed`,
        { amount, reason: 'Aporte via painel da roleta' }, { headers: authHeaders() });
      toast.success(`Aporte de ${fmtN(amount)} feito. Fundo global: ${fmtN(r.data.balance)}`);
      setSeedAmount(''); await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro no aporte'); }
    setBusy(false);
  };

  const doAllocate = async () => {
    const amount = parseInt(allocAmount, 10);
    if (!amount || amount <= 0) return toast.error('Informe o valor da alocação');
    setBusy(true);
    try {
      const r = await axios.post(`${API}/event-fund/admin/allocate`,
        { campaign_id: 'roleta-azulzinhas', amount, reason: 'Alocação via painel da roleta' },
        { headers: authHeaders() });
      toast.success(`Alocado: ${fmtN(r.data.splits.regular_pool)} regular / ${fmtN(r.data.splits.jackpot_pool)} jackpot / ${fmtN(r.data.splits.safety_reserve)} reserva`);
      setAllocAmount(''); await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro na alocação'); }
    setBusy(false);
  };

  const saveEndDate = async (value) => {
    setBusy(true);
    try {
      await axios.post(`${API}/event-campaigns/roulette/end-date`, { end_date: value },
        { headers: authHeaders() });
      toast.success(value ? 'Data de encerramento definida' : 'Data de encerramento removida');
      setEndDate(''); await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao definir data'); }
    setBusy(false);
  };

  const saveBoostTiers = async () => {
    const tiers = [];
    for (const t of boostTiers) {
      const pool = parseInt(t.min_pool, 10);
      const pct100 = parseFloat(String(t.percent).replace(',', '.'));
      if (!pool || pool <= 0 || !pct100 || pct100 <= 0) {
        return toast.error('Preencha pool e chance (%) de todos os degraus');
      }
      if (pct100 > 20) return toast.error('Chance máxima por degrau é 20% (proteção contra farm)');
      tiers.push({ min_pool: pool, prob_100x: Math.round(pct100 * 10000) });
    }
    setBusy(true);
    try {
      const r = await axios.put(`${API}/event-campaigns/roulette/boost-tiers`,
        { tiers }, { headers: authHeaders() });
      toast.success(r.data.policy_version_bumped
        ? `Degraus salvos — política atualizada p/ v${r.data.policy_version}`
        : 'Degraus salvos (sem mudanças)');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao salvar degraus'); }
    setBusy(false);
  };

  const saveRegularTiers = async () => {
    const tiers = [];
    for (const t of regularTiers) {
      const pool = parseInt(t.min_pool, 10);
      const pct = parseFloat(String(t.percent).replace(',', '.'));
      if (!pool || pool <= 0 || !pct || pct <= 0) {
        return toast.error('Preencha pool e chance (%) de todos os degraus do 2X');
      }
      if (pct > 60) return toast.error('Chance máxima do 2X por degrau é 60%');
      tiers.push({ min_pool: pool, prob_2x: Math.round(pct * 10000) });
    }
    setBusy(true);
    try {
      const r = await axios.put(`${API}/event-campaigns/roulette/regular-boost-tiers`,
        { tiers }, { headers: authHeaders() });
      toast.success(r.data.policy_version_bumped
        ? `Degraus do 2X salvos — política atualizada p/ v${r.data.policy_version}`
        : 'Degraus do 2X salvos (sem mudanças)');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao salvar degraus do 2X'); }
    setBusy(false);
  };

  const doReconcile = async () => {
    setBusy(true);
    try {
      const r = await axios.get(`${API}/event-fund/admin/reconcile`, { headers: authHeaders() });
      setReconcile(r.data);
      r.data.ok ? toast.success('Reconciliação OK — ledger bate com os saldos')
        : toast.error('DIVERGÊNCIA detectada na reconciliação!');
    } catch { toast.error('Erro na reconciliação'); }
    setBusy(false);
  };

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div>;
  if (!m) return <p className="text-sm text-gray-500 py-6 text-center">Não foi possível carregar.</p>;

  const st = m.campaign.status;
  const payoutHigh = m.spins.total >= 50 && m.economy.payout_real > m.economy.payout_expected * 1.15;

  return (
    <div className="space-y-4" data-testid="roulette-campaign-management">
      {/* prêmios que ainda não chegaram na conta do ganhador */}
      <RoulettePrizeFulfillment />

      {/* status + ações */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle className="text-lg flex items-center gap-2">
              🎡 {m.campaign.name}
              <Badge className={STATUS_STYLE[st] || 'bg-gray-100 text-gray-600'} data-testid="roulette-campaign-status">{st}</Badge>
              <Badge className="bg-blue-100 text-blue-700" data-testid="roulette-edition-badge">Edição #{m.campaign.edition || 1}</Badge>
              <span className="text-xs text-gray-400 font-normal">política v{m.campaign.policy_version}</span>
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={load} data-testid="roulette-refresh-button">
                <RefreshCw className="w-4 h-4" />
              </Button>
              {st === 'ENDED' && (
                <>
                  <Button size="sm" disabled={busy} onClick={startNewEdition}
                    className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="roulette-new-edition-button">
                    <Rocket className="w-4 h-4 mr-1" /> Nova Edição (zerar)
                  </Button>
                  <Button size="sm" disabled={busy} onClick={resumeEdition}
                    className="bg-green-600 hover:bg-green-700 text-white" data-testid="roulette-resume-button">
                    <Play className="w-4 h-4 mr-1" /> Retomar mesma edição
                  </Button>
                </>
              )}
              {st !== 'ACTIVE' && st !== 'ENDED' && (
                <Button size="sm" disabled={busy} onClick={() => changeStatus('ACTIVE', 'ativação via painel')}
                  className="bg-green-600 hover:bg-green-700 text-white" data-testid="roulette-activate-button">
                  <Play className="w-4 h-4 mr-1" /> Ativar
                </Button>
              )}
              {st === 'ACTIVE' && (
                <>
                  <Button size="sm" disabled={busy}
                    onClick={() => window.confirm('⛔ PAUSA DE EMERGÊNCIA: novos giros e recompensas serão bloqueados imediatamente. Confirmar?')
                      && changeStatus('PAUSED', 'pausa de emergência via painel')}
                    className="bg-red-600 hover:bg-red-700 text-white" data-testid="roulette-emergency-pause-button">
                    <ShieldAlert className="w-4 h-4 mr-1" /> Pausa de Emergência
                  </Button>
                  <Button size="sm" variant="outline" disabled={busy}
                    onClick={() => window.confirm('🏁 ENCERRAR EVENTO: o evento fica indisponível para os clientes. Depois você poderá iniciar uma NOVA EDIÇÃO (zerada) ou RETOMAR esta mesma edição. Confirmar?')
                      && changeStatus('ENDED', 'encerramento via painel')}
                    className="border-slate-400 text-slate-700" data-testid="roulette-end-button">
                    🏁 Encerrar
                  </Button>
                </>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-2">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Stat label="Fundo Global" value={fmtN(m.global_fund.balance)} testId="stat-global-fund"
              hint="Saldo parado no global — as vendas entram e são alocadas na hora p/ os pools" />
            <Stat label="Arrecadado das vendas (0,5%)" value={fmtN(m.global_fund.recharge_contribution_total || 0)}
              testId="stat-recharge-contrib"
              hint={`${m.global_fund.recharge_contribution_count || 0} compra(s) contribuíram — valor já distribuído nos pools (70/10/20)`} />
            <Stat label="Aportado" value={`${fmtN(m.global_fund.seeded_total)} / ${fmtN(m.global_fund.seed_cap)}`} testId="stat-seeded" />
            <Stat label="Jackpot" value={m.jackpot_active ? (m.jackpot_boost?.boosted ? '🚀 BOOST' : '💎 ATIVO') : 'Acumulando'}
              accent={m.jackpot_boost?.boosted ? 'text-orange-600' : m.jackpot_active ? 'text-yellow-600' : 'text-gray-500'} testId="stat-jackpot-active"
              hint={m.jackpot_boost?.boosted
                ? `Jackpot progressivo: pool acumulado elevou a chance p/ ${(m.jackpot_boost.prob_100x_ppm / 10000).toFixed(2).replace('.', ',')}% por giro (base ${(m.jackpot_boost.base_prob_100x_ppm / 10000).toFixed(2).replace('.', ',')}%)`
                : `Chance atual do 100X: ${((m.jackpot_boost?.prob_100x_ppm ?? 2600) / 10000).toFixed(2).replace('.', ',')}% por giro — sobe p/ 2% com pool ≥200k e 5% com ≥300k`} />
            <Stat label="Prêmios reservados" value={fmtN(m.campaign_fund.reserved_prizes)} testId="stat-reserved" />
          </div>
        </CardContent>
      </Card>

      {/* fundos da campanha */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2"><PiggyBank className="w-4 h-4 text-blue-600" /> Fundos da Campanha</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Stat label="Regular (2X)" value={fmtN(m.campaign_fund.regular_pool)} accent="text-emerald-600" testId="stat-regular-pool" />
            <Stat label="Jackpot (100X)" value={fmtN(m.campaign_fund.jackpot_pool)} accent="text-yellow-600" testId="stat-jackpot-pool" />
            <Stat label="Reserva de segurança" value={fmtN(m.campaign_fund.safety_reserve)} testId="stat-safety-reserve" />
            <Stat label="Prêmios liquidados" value={fmtN(m.campaign_fund.lifetime_prizes_settled)} testId="stat-settled" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="flex gap-2">
              <Input type="number" placeholder="Aporte no fundo global" value={seedAmount}
                onChange={(e) => setSeedAmount(e.target.value)} data-testid="seed-amount-input" />
              <Button disabled={busy} onClick={doSeed} data-testid="seed-submit-button"
                className="bg-blue-600 hover:bg-blue-700 text-white shrink-0">Aportar</Button>
            </div>
            <div className="flex gap-2">
              <Input type="number" placeholder="Alocar p/ roleta (70/10/20)" value={allocAmount}
                onChange={(e) => setAllocAmount(e.target.value)} data-testid="alloc-amount-input" />
              <Button disabled={busy} onClick={doAllocate} data-testid="alloc-submit-button"
                className="bg-indigo-600 hover:bg-indigo-700 text-white shrink-0">Alocar</Button>
            </div>
          </div>

          {/* Transferência entre fundos da campanha */}
          <div className="border-t pt-3 mt-3 space-y-2" data-testid="pool-transfer-panel">
            <p className="text-sm font-semibold">🔁 Transferir entre fundos</p>
            <p className="text-[11px] text-gray-500">
              Move saldo de um pool para outro (ex: 2X com sobra → Jackpot). Fica registrado no livro-razão com o admin responsável.
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <select value={transferForm.from_pool} data-testid="transfer-from-select"
                onChange={(e) => setTransferForm({ ...transferForm, from_pool: e.target.value })}
                className="h-9 rounded-md border px-2 text-sm bg-white">
                <option value="regular_pool">2X (regular)</option>
                <option value="jackpot_pool">Jackpot (100X)</option>
                <option value="safety_reserve">Reserva</option>
              </select>
              <span className="text-gray-400 text-sm">→</span>
              <select value={transferForm.to_pool} data-testid="transfer-to-select"
                onChange={(e) => setTransferForm({ ...transferForm, to_pool: e.target.value })}
                className="h-9 rounded-md border px-2 text-sm bg-white">
                <option value="jackpot_pool">Jackpot (100X)</option>
                <option value="regular_pool">2X (regular)</option>
                <option value="safety_reserve">Reserva</option>
              </select>
              <Input type="number" placeholder="Valor" value={transferForm.amount}
                onChange={(e) => setTransferForm({ ...transferForm, amount: e.target.value })}
                data-testid="transfer-amount-input" className="w-36 h-9" />
              <Button disabled={busy} onClick={doTransfer} data-testid="transfer-submit-button"
                className="bg-amber-600 hover:bg-amber-700 text-white shrink-0">Transferir</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Relatório da Banca — resumo diário */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            📊 Relatório da Banca <span className="text-xs font-normal text-muted-foreground">(diário · fuso Brasília · últimos 14 dias)</span>
          </CardTitle>
        </CardHeader>
        <CardContent data-testid="daily-report-panel">
          {daily && (
            <div className="mb-3 grid grid-cols-2 gap-2 sm:grid-cols-4 text-center">
              <div className="rounded-lg bg-blue-50 p-2"><p className="text-[10px] text-muted-foreground">Pool 2X</p><p className="text-sm font-bold text-blue-700" data-testid="dr-pool-regular">{fmtN(daily.pools.regular_pool)}</p></div>
              <div className="rounded-lg bg-amber-50 p-2"><p className="text-[10px] text-muted-foreground">Pool Jackpot</p><p className="text-sm font-bold text-amber-700" data-testid="dr-pool-jackpot">{fmtN(daily.pools.jackpot_pool)}</p></div>
              <div className="rounded-lg bg-slate-100 p-2"><p className="text-[10px] text-muted-foreground">Reserva</p><p className="text-sm font-bold" data-testid="dr-pool-reserve">{fmtN(daily.pools.safety_reserve)}</p></div>
              <div className="rounded-lg bg-purple-50 p-2"><p className="text-[10px] text-muted-foreground">Prêmios a entregar</p><p className="text-sm font-bold text-purple-700" data-testid="dr-pool-reserved">{fmtN(daily.pools.reserved_prizes)}</p></div>
            </div>
          )}
          {!daily?.report?.length ? (
            <p className="py-4 text-center text-sm text-muted-foreground">Sem giros no período.</p>
          ) : (
            <div className="max-h-72 overflow-auto rounded-lg border">
              <table className="w-full text-xs" data-testid="daily-report-table">
                <thead className="sticky top-0 bg-slate-100 text-[10px] uppercase text-muted-foreground">
                  <tr>
                    <th className="px-2 py-1.5 text-left">Dia</th>
                    <th className="px-2 py-1.5 text-right">Giros</th>
                    <th className="px-2 py-1.5 text-right">2X</th>
                    <th className="px-2 py-1.5 text-right">100X</th>
                    <th className="px-2 py-1.5 text-right">Entradas 🔵</th>
                    <th className="px-2 py-1.5 text-right">Prêmios 🟡</th>
                    <th className="px-2 py-1.5 text-right">Resultado</th>
                    <th className="px-2 py-1.5 text-right">Jogadores</th>
                  </tr>
                </thead>
                <tbody>
                  {daily.report.map((d) => (
                    <tr key={d.date} className="border-t" data-testid={`dr-row-${d.date}`}>
                      <td className="px-2 py-1.5 font-medium">{d.date.slice(8, 10)}/{d.date.slice(5, 7)}</td>
                      <td className="px-2 py-1.5 text-right">{fmtN(d.spins)}</td>
                      <td className="px-2 py-1.5 text-right">{fmtN(d.win_2x)} <span className="text-muted-foreground">({fmtN(d.prizes_2x)})</span></td>
                      <td className="px-2 py-1.5 text-right font-semibold text-amber-700">{fmtN(d.win_100x)} <span className="font-normal text-muted-foreground">({fmtN(d.prizes_100x)})</span></td>
                      <td className="px-2 py-1.5 text-right text-blue-700">{fmtN(d.blue_in)}</td>
                      <td className="px-2 py-1.5 text-right text-amber-700">{fmtN(d.prizes_total)}</td>
                      <td className={`px-2 py-1.5 text-right font-bold ${d.net >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{d.net >= 0 ? '+' : ''}{fmtN(d.net)}</td>
                      <td className="px-2 py-1.5 text-right">{fmtN(d.unique_users)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Jackpot Progressivo — degraus editáveis */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Rocket className="w-4 h-4 text-orange-600" /> Jackpot Progressivo (degraus)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3" data-testid="boost-tiers-panel">
          <p className="text-[11px] text-gray-500">
            Quando o pool do Jackpot acumula além de um degrau, a chance do 100X sobe para o valor do degrau.
            Base (abaixo do 1º degrau): <b>{((m.jackpot_boost?.base_prob_100x_ppm ?? 2600) / 10000).toFixed(2).replace('.', ',')}%</b> por giro.
            Cada Jackpot pago reduz o pool e a chance volta sozinha.
          </p>
          {boostTiers.length === 0 && (
            <p className="text-xs text-gray-400" data-testid="boost-tiers-empty">Nenhum degrau — boost desativado (chance sempre na base).</p>
          )}
          {boostTiers.map((t, i) => (
            <div key={i} className="flex flex-wrap items-center gap-2" data-testid={`boost-tier-row-${i}`}>
              <span className="text-xs text-gray-500 w-16">Degrau {i + 1}</span>
              <div className="flex items-center gap-1">
                <span className="text-[11px] text-gray-500">Pool ≥</span>
                <Input type="number" min="50000" step="10000" className="w-32 h-8 text-sm"
                  value={t.min_pool} data-testid={`boost-tier-pool-${i}`}
                  onChange={(e) => setBoostTiers(boostTiers.map((x, j) => j === i ? { ...x, min_pool: e.target.value } : x))} />
              </div>
              <div className="flex items-center gap-1">
                <span className="text-[11px] text-gray-500">Chance</span>
                <Input type="number" min="0.01" max="20" step="0.5" className="w-24 h-8 text-sm"
                  value={t.percent} data-testid={`boost-tier-percent-${i}`}
                  onChange={(e) => setBoostTiers(boostTiers.map((x, j) => j === i ? { ...x, percent: e.target.value } : x))} />
                <span className="text-[11px] text-gray-500">%</span>
              </div>
              <span className="text-[11px] text-gray-400">
                {parseFloat(String(t.percent).replace(',', '.')) > 0
                  ? `~1 a cada ${Math.round(100 / parseFloat(String(t.percent).replace(',', '.')))} giros`
                  : ''}
              </span>
              <Button size="sm" variant="ghost" className="h-8 px-2 text-red-500 hover:text-red-700"
                data-testid={`boost-tier-remove-${i}`}
                onClick={() => setBoostTiers(boostTiers.filter((_, j) => j !== i))}>✕</Button>
            </div>
          ))}
          <div className="flex flex-wrap gap-2">
            {boostTiers.length < 5 && (
              <Button size="sm" variant="outline" disabled={busy} data-testid="boost-tier-add-button"
                onClick={() => setBoostTiers([...boostTiers, { min_pool: '', percent: '' }])}>
                + Adicionar degrau
              </Button>
            )}
            <Button size="sm" disabled={busy} onClick={saveBoostTiers}
              className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="boost-tiers-save-button">
              Salvar degraus
            </Button>
          </div>
          <p className="text-[10px] text-gray-400">
            Limites de segurança: máx. 5 degraus, pools em ordem crescente, chance máx. 20% por degrau. Salvar cria uma nova versão da política (giros antigos ficam auditados com a regra da época).
          </p>

          {/* 2X Progressivo */}
          <div className="border-t pt-3 mt-2 space-y-3" data-testid="boost2x-panel">
            <p className="text-sm font-semibold flex items-center gap-1.5">💰 2X Progressivo (pool regular)</p>
            <p className="text-[11px] text-gray-500">
              Quando o pool regular (2X) acumula além de um degrau, a chance do 2X sobe.
              Base: <b>{((m.regular_boost?.base_prob_2x_ppm ?? 370000) / 10000).toFixed(0)}%</b> por giro.
              {m.regular_boost?.boosted && (
                <span className="text-orange-600 font-bold"> 🚀 BOOST ativo: {((m.regular_boost.prob_2x_ppm) / 10000).toFixed(0)}%</span>
              )}
            </p>
            {regularTiers.length === 0 && (
              <p className="text-xs text-gray-400" data-testid="boost2x-empty">Nenhum degrau — 2X sempre na base.</p>
            )}
            {regularTiers.map((t, i) => (
              <div key={i} className="flex flex-wrap items-center gap-2" data-testid={`boost2x-row-${i}`}>
                <span className="text-xs text-gray-500 w-16">Degrau {i + 1}</span>
                <div className="flex items-center gap-1">
                  <span className="text-[11px] text-gray-500">Pool ≥</span>
                  <Input type="number" min="1000" step="10000" className="w-32 h-8 text-sm"
                    value={t.min_pool} data-testid={`boost2x-pool-${i}`}
                    onChange={(e) => setRegularTiers(regularTiers.map((x, j) => j === i ? { ...x, min_pool: e.target.value } : x))} />
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-[11px] text-gray-500">Chance</span>
                  <Input type="number" min="1" max="60" step="1" className="w-24 h-8 text-sm"
                    value={t.percent} data-testid={`boost2x-percent-${i}`}
                    onChange={(e) => setRegularTiers(regularTiers.map((x, j) => j === i ? { ...x, percent: e.target.value } : x))} />
                  <span className="text-[11px] text-gray-500">%</span>
                </div>
                <Button size="sm" variant="ghost" className="h-8 px-2 text-red-500 hover:text-red-700"
                  data-testid={`boost2x-remove-${i}`}
                  onClick={() => setRegularTiers(regularTiers.filter((_, j) => j !== i))}>✕</Button>
              </div>
            ))}
            <div className="flex flex-wrap gap-2">
              {regularTiers.length < 5 && (
                <Button size="sm" variant="outline" disabled={busy} data-testid="boost2x-add-button"
                  onClick={() => setRegularTiers([...regularTiers, { min_pool: '', percent: '' }])}>
                  + Adicionar degrau
                </Button>
              )}
              <Button size="sm" disabled={busy} onClick={saveRegularTiers}
                className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="boost2x-save-button">
                Salvar degraus do 2X
              </Button>
            </div>
            <p className="text-[10px] text-gray-400">
              Chance máx. do 2X por degrau: 60%. A soma das chances máximas (2X + Jackpot) nunca pode passar de 100%.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* encerramento automático */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <CalendarClock className="w-4 h-4 text-orange-600" /> Encerramento Automático
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {m.campaign.ends_at ? (
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-orange-200 bg-orange-50 p-3"
              data-testid="end-date-panel">
              <p className="text-sm text-gray-700">
                Evento encerra automaticamente em{' '}
                <b data-testid="end-date-value">
                  {new Date(m.campaign.ends_at).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' })}
                </b>{' '}
                (fim do dia, horário de Brasília)
              </p>
              <Button size="sm" variant="outline" disabled={busy}
                onClick={() => window.confirm('Remover a data de encerramento automático?') && saveEndDate(null)}
                data-testid="end-date-remove-button">Remover</Button>
            </div>
          ) : (
            <p className="text-sm text-gray-500" data-testid="end-date-empty">
              Sem data limite — o evento só encerra manualmente. Defina uma data para encerrar sozinho às 23:59 daquele dia.
            </p>
          )}
          <div className="flex gap-2 max-w-sm">
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
              data-testid="end-date-input" />
            <Button disabled={busy || !endDate} onClick={() => saveEndDate(endDate)}
              className="bg-orange-600 hover:bg-orange-700 text-white shrink-0"
              data-testid="end-date-save-button">Definir</Button>
          </div>
        </CardContent>
      </Card>

      {/* métricas */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">📊 Giros & Payout</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Stat label="Total de giros" value={fmtN(m.spins.total)} testId="stat-spins-total" />
            <Stat label="Tente novamente" value={fmtN(m.spins.lose)} testId="stat-spins-lose" />
            <Stat label="2X" value={fmtN(m.spins.win_2x)} accent="text-emerald-600" testId="stat-spins-2x" />
            <Stat label="100X" value={fmtN(m.spins.win_100x)} accent="text-yellow-600" testId="stat-spins-100x" />
            <Stat label="Participantes" value={fmtN(m.spins.unique_users)} testId="stat-users" />
            <Stat label="Giros/usuário" value={m.spins.avg_spins_user} testId="stat-avg-spins" />
            <Stat label="Reembolsados" value={fmtN(m.spins.refunded)} testId="stat-refunded" />
            <Stat label="Prêmios pagos" value={fmtN(m.economy.prizes_total)} accent="text-yellow-600" testId="stat-prizes-total" />
          </div>
          <div className={`rounded-lg border p-3 ${payoutHigh ? 'border-red-300 bg-red-50' : 'bg-gray-50'}`}
            data-testid="payout-panel">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm font-bold text-gray-700">Payout real vs esperado</p>
              <p className="text-sm">
                <b className={payoutHigh ? 'text-red-600' : 'text-gray-900'} data-testid="payout-real">{pct(m.economy.payout_real)}</b>
                <span className="text-gray-400"> / esperado </span>
                <b data-testid="payout-expected">{pct(m.economy.payout_expected)}</b>
              </p>
            </div>
            {payoutHigh && <p className="text-xs text-red-600 mt-1">⚠️ Payout acima do esperado — verifique a reconciliação. Variação estatística é normal em amostras pequenas.</p>}
            <p className="text-[11px] text-gray-500 mt-1">
              Azulzinhas emitidas: <b>{fmtN(m.economy.blue_issued)}</b> · gastas em giros: <b>{fmtN(m.economy.blue_spent)}</b>
              {Object.keys(m.fulfillment).length > 0 && (
                <> · entregas: {Object.entries(m.fulfillment).map(([k, v]) => `${k}=${v}`).join(', ')}</>
              )}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* reconciliação */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2"><Scale className="w-4 h-4 text-purple-600" /> Reconciliação</CardTitle>
            <Button size="sm" variant="outline" disabled={busy} onClick={doReconcile} data-testid="reconcile-button">
              Verificar agora
            </Button>
          </div>
        </CardHeader>
        {reconcile && (
          <CardContent className="pt-0">
            <div className={`rounded-lg border p-3 text-sm ${reconcile.ok ? 'border-green-300 bg-green-50' : 'border-red-300 bg-red-50'}`}
              data-testid="reconcile-result">
              <p className="font-bold">{reconcile.ok ? '✅ Ledger bate com todos os saldos' : '🚨 DIVERGÊNCIA — não corrigir manualmente, investigar!'}</p>
              {reconcile.funds.map((f) => (
                <p key={f.fund_id} className="text-xs mt-1">
                  {f.ok ? '✓' : '✗'} {f.fund_id}: snapshot {fmtN(f.snapshot)} · ledger {fmtN(f.ledger_sum)}
                </p>
              ))}
              {reconcile.stale_pending?.length > 0 && (
                <p className="text-xs text-red-600 mt-1">⚠️ {reconcile.stale_pending.length} lançamento(s) pendente(s) antigo(s)</p>
              )}
            </div>
          </CardContent>
        )}
      </Card>

      {st === 'PAUSED' && (
        <div className="rounded-lg border border-orange-300 bg-orange-50 p-3 text-sm text-orange-700" data-testid="paused-notice">
          A campanha está PAUSADA: nenhum giro novo e nenhuma Azulzinha de recarga é gerada. Use <b>Ativar</b> para retomar.
        </div>
      )}

      {st === 'ENDED' && (
        <div className="rounded-lg border border-blue-300 bg-blue-50 p-3 text-sm text-blue-700" data-testid="ended-notice">
          Evento ENCERRADO. Escolha: <b>Nova Edição (zerar)</b> arquiva tudo para auditoria e recomeça do zero (pools voltam ao Fundo Global) · <b>Retomar mesma edição</b> continua de onde parou.
        </div>
      )}

      {/* Edições anteriores (auditoria) */}
      <Card data-testid="roulette-editions-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            📜 Edições Anteriores
            <span className="text-xs text-gray-400 font-normal">arquivadas para auditoria</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-2">
          {editions.length === 0 ? (
            <p className="text-sm text-gray-500" data-testid="editions-empty">Nenhuma edição arquivada ainda — ao iniciar uma Nova Edição, a anterior aparece aqui.</p>
          ) : (
            <div className="space-y-2">
              {editions.map((ed) => (
                <details key={ed.id} className="rounded-lg border bg-white" data-testid={`edition-item-${ed.edition}`}>
                  <summary className="cursor-pointer px-3 py-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                    <span className="font-black text-blue-700">Edição #{ed.edition}</span>
                    <span className="text-gray-500 text-xs">
                      {new Date(ed.started_at).toLocaleDateString('pt-BR')} → {new Date(ed.ended_at).toLocaleDateString('pt-BR')}
                    </span>
                    <span className="text-xs">🎡 {fmtN(ed.spins?.total)} giros</span>
                    <span className="text-xs">👥 {fmtN(ed.spins?.unique_users)} jogadores</span>
                    <span className="text-xs">💎 {fmtN(ed.spins?.win_100x)} jackpot(s)</span>
                    <span className="text-xs text-yellow-700">🏆 {fmtN(ed.economy?.prizes_total)} pagos</span>
                  </summary>
                  <div className="px-3 pb-3 pt-1 border-t text-xs space-y-2">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      <Stat label="Azulzinhas gastas" value={fmtN(ed.economy?.blue_spent)} />
                      <Stat label="Vitórias 2X" value={fmtN(ed.spins?.win_2x)} />
                      <Stat label="Jackpots 100X" value={fmtN(ed.spins?.win_100x)} accent="text-yellow-600" />
                      <Stat label="Devolvido ao Global" value={fmtN(ed.swept_total)} accent="text-blue-600" />
                    </div>
                    <p className="text-gray-500">
                      Pools no encerramento — 2X: {fmtN(ed.pools_final?.regular_pool)} · Jackpot: {fmtN(ed.pools_final?.jackpot_pool)} · Reserva: {fmtN(ed.pools_final?.safety_reserve)}
                    </p>
                    {(ed.final_ranking || []).length > 0 && (
                      <div>
                        <p className="font-semibold text-gray-700 mb-1">🏅 Ranking final (top {Math.min(ed.final_ranking.length, 20)})</p>
                        <div className="space-y-0.5">
                          {ed.final_ranking.slice(0, 10).map((r) => (
                            <p key={r.rank} className="text-gray-600">
                              {r.rank}º {r.name} <span className="text-gray-400">({r.kako_masked})</span> — {fmtN(r.spins)} giros · {fmtN(r.prizes)} em prêmios
                            </p>
                          ))}
                        </div>
                      </div>
                    )}
                    {(ed.winners_100x || []).length > 0 && (
                      <div>
                        <p className="font-semibold text-yellow-700 mb-1">💎 Ganhadores do Jackpot</p>
                        {ed.winners_100x.map((w, i) => (
                          <p key={i} className="text-gray-600">{w.name} ({w.kako_masked}) — +{fmtN(w.prize)}</p>
                        ))}
                      </div>
                    )}
                  </div>
                </details>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
